import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { generatedReportsService, PublicReportDto, PublicReportItemDto } from '@inferium/shared/src/api/generatedReportsService';
import { ReportPdfGenerator } from '@inferium/shared/src/utils/pdfUtils';
import { fetchWithS3Fallback } from '@inferium/shared/src/utils/s3Fallback';
import './PublicReportPage.css';

const PublicReportPage: React.FC = () => {
  const { token } = useParams<{ companySlug: string; clientSlug: string; token: string }>();
  const [report, setReport] = useState<PublicReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state (global)
  const [showNormal, setShowNormal] = useState(true);
  const [showIncidents, setShowIncidents] = useState(true);

  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // PDF export state
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetchReport = async () => {
      try {
        setLoading(true);
        const data = await fetchWithS3Fallback(
          () => generatedReportsService.getPublicReport(token),
          `public-reports/${token}.json`
        );
        setReport(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Report not found or could not be loaded.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [token]);

  // Multi-site detection
  const uniqueSiteNames = useMemo(() => {
    if (!report) return [];
    const sites = new Set(
      report.reports
        .map(r => (r.siteName || '').trim())
        .filter(s => s.length > 0)
    );
    return Array.from(sites).sort();
  }, [report]);

  const isMultiSite = uniqueSiteNames.length >= 2;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const getDateRange = () => {
    if (!report) return '';
    const from = formatDate(report.fromDate);
    const to = formatDate(report.toDate);
    return from === to ? from : `${from} - ${to}`;
  };

  const filterReports = (items: PublicReportItemDto[]) => {
    return items.filter((r: PublicReportItemDto) => {
      const isNormal = r.incidentStatus.toLowerCase() === 'normal';
      if (isNormal && !showNormal) return false;
      if (!isNormal && !showIncidents) return false;
      return true;
    });
  };

  const filteredReports = filterReports(report?.reports ?? []);

  const normalCount = report?.reports.filter(r => r.incidentStatus.toLowerCase() === 'normal').length ?? 0;
  const incidentCount = report?.reports.filter(r => r.incidentStatus.toLowerCase() !== 'normal').length ?? 0;

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
  };

  const handleExportPdf = useCallback(async () => {
    if (isExporting || !report) return;
    setIsExporting(true);
    try {
      const container = document.querySelector('[data-pdf-container="true"]') as HTMLElement;
      if (!container) throw new Error('Report container not found');
      const pdfTitle = (report.title || 'Security Report').replace(/^Report:\s*/i, '');
      await ReportPdfGenerator.generateReportPdf(container, pdfTitle);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, report]);

  const renderReportItem = (item: PublicReportItemDto, index: number) => {
    const visibleImages = item.images.filter((url: string) => !url.includes('/compressed/'));
    return (
      <div key={index} className={`report-item${item.incidentStatus.toLowerCase() === 'extreme' ? ' report-item-extreme' : ''}`}>
        <div className="report-item-header">
          <h3 className="report-item-title">{item.aiTitle}</h3>
          <span className={`report-item-status report-status-${item.incidentStatus.toLowerCase()}`}>
            {item.incidentStatus.toLowerCase() === 'extreme' ? 'Incident' : item.incidentStatus}
          </span>
        </div>
        <div className="report-item-meta">
          <span>{formatDateTime(item.reportedAt)}</span>
          {isMultiSite && item.siteName && (
            <>
              <span className="report-meta-separator">|</span>
              <span>{item.siteName}</span>
            </>
          )}
          {item.guardName && (
            <>
              <span className="report-meta-separator">|</span>
              <span>{item.guardName}</span>
            </>
          )}
        </div>
        {item.smartRevision && (
          <p className="report-item-details">{item.smartRevision}</p>
        )}
        {visibleImages.length > 0 && (
          <div className="report-item-images">
            {visibleImages.map((url: string, idx: number) => (
              <img
                key={idx}
                src={url}
                alt={`Report image ${idx + 1}`}
                className="report-item-image"
                onClick={() => openLightbox(visibleImages, idx)}
                loading="lazy"
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="report-page">
        <div className="report-container">
          <div className="report-loading">Loading report...</div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-page">
        <div className="report-container">
          <div className="report-error">{error || 'Report not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-page">
      <div className="report-container" data-pdf-container="true">
        {/* Header */}
        <div className="report-header">
          <h1 className="report-title">{(report.title || '').replace(/^Report:\s*/i, '')}</h1>
          <div className="report-meta">
            <span className="report-company-name">{report.companyName}</span>
            <span className="report-meta-separator">|</span>
            {isMultiSite ? (
              <span className="report-site-name">{uniqueSiteNames.join(', ')}</span>
            ) : (
              <span className="report-site-name">{report.siteName}</span>
            )}
          </div>
          <div className="report-date-range">{getDateRange()}</div>
          <div className="report-header-bottom">
            {report.companyLogo && (
              <img src={report.companyLogo} alt={report.companyName} className="report-company-logo" />
            )}
            <button
              className="report-export-btn"
              onClick={handleExportPdf}
              disabled={isExporting}
              data-html2canvas-ignore="true"
            >
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {/* Global Summary - hide when only 1 report item */}
        {report.reports.length > 1 && (
          <div className="report-summary">
            <div className="report-summary-item">
              <span className="report-summary-count">{report.reports.length}</span>
              <span className="report-summary-label">Total Reports</span>
            </div>
            <button
              className={`report-summary-item report-summary-clickable report-summary-normal-filter${!showNormal ? ' report-summary-inactive' : ''}`}
              onClick={() => setShowNormal(prev => !prev)}
            >
              <span className="report-summary-count report-summary-normal">{normalCount}</span>
              <span className="report-summary-label">Normal</span>
            </button>
            <button
              className={`report-summary-item report-summary-clickable report-summary-incident-filter${!showIncidents ? ' report-summary-inactive' : ''}`}
              onClick={() => setShowIncidents(prev => !prev)}
            >
              <span className="report-summary-count report-summary-incident">{incidentCount}</span>
              <span className="report-summary-label">Incidents</span>
            </button>
          </div>
        )}

        {/* Overview - hide when only 1 report item */}
        {report.reports.length > 1 && report.overviewSummary && (
          <div className="report-overview">
            <h2>Overview</h2>
            <p>{report.overviewSummary}</p>
          </div>
        )}

        {/* Report Items */}
        {isMultiSite ? (
          // Multi-site: group by site name
          <>
            <h2 className="report-section-title">{report.reports.length === 1 ? 'Detailed Report' : 'Detailed Reports'}</h2>
            {uniqueSiteNames.map(site => {
              const siteReports = report.reports.filter(r => (r.siteName || '').trim() === site);
              const siteFiltered = filterReports(siteReports);
              const siteNormal = siteReports.filter(r => r.incidentStatus.toLowerCase() === 'normal').length;
              const siteIncident = siteReports.filter(r => r.incidentStatus.toLowerCase() !== 'normal').length;

              return (
                <div key={site} className="report-site-section">
                  <div className="report-site-section-header">
                    <h3 className="report-site-section-title">{site}</h3>
                    <div className="report-site-section-counts">
                      <span className="report-site-count">{siteReports.length} Total</span>
                      <span className="report-site-count-separator">/</span>
                      <span className="report-site-count report-site-count-normal">{siteNormal} Normal</span>
                      <span className="report-site-count-separator">/</span>
                      <span className="report-site-count report-site-count-incident">{siteIncident} Incidents</span>
                    </div>
                  </div>
                  {siteFiltered.map(renderReportItem)}
                  {siteFiltered.length === 0 && siteReports.length > 0 && (
                    <div className="report-empty">
                      No reports match the current filter for this site.
                    </div>
                  )}
                </div>
              );
            })}
            {/* Reports with no site name (edge case) */}
            {(() => {
              const unlocated = report.reports.filter(r => !(r.siteName || '').trim());
              if (unlocated.length === 0) return null;
              const unlocatedFiltered = filterReports(unlocated);
              const unlocatedNormal = unlocated.filter(r => r.incidentStatus.toLowerCase() === 'normal').length;
              const unlocatedIncident = unlocated.filter(r => r.incidentStatus.toLowerCase() !== 'normal').length;
              return (
                <div className="report-site-section">
                  <div className="report-site-section-header">
                    <h3 className="report-site-section-title">Other</h3>
                    <div className="report-site-section-counts">
                      <span className="report-site-count">{unlocated.length} Total</span>
                      <span className="report-site-count-separator">/</span>
                      <span className="report-site-count report-site-count-normal">{unlocatedNormal} Normal</span>
                      <span className="report-site-count-separator">/</span>
                      <span className="report-site-count report-site-count-incident">{unlocatedIncident} Incidents</span>
                    </div>
                  </div>
                  {unlocatedFiltered.map(renderReportItem)}
                </div>
              );
            })()}
          </>
        ) : (
          // Single-site: flat list (existing layout)
          <>
            <h2 className="report-section-title">{report.reports.length === 1 ? 'Detailed Report' : 'Detailed Reports'}</h2>
            {filteredReports.map(renderReportItem)}

            {filteredReports.length === 0 && report.reports.length > 0 && (
              <div className="report-empty">
                No reports match the current filter. Click the status categories above to adjust.
              </div>
            )}

            {report.reports.length === 0 && (
              <div className="report-empty">
                No reports were found for the specified date range.
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="report-footer">
          <img src="/InferiumTitle.png" alt="Inferium Reports" className="report-footer-logo" />
          <span>Generated by Inferium Reports for {report.companyName}</span>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
            <button
              className="lightbox-nav lightbox-prev"
              onClick={() => setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)}
              style={{ visibility: lightboxImages.length > 1 ? 'visible' : 'hidden' }}
            >
              &#8249;
            </button>
            <img src={lightboxImages[lightboxIndex]} alt="Report" className="lightbox-image" />
            <button
              className="lightbox-nav lightbox-next"
              onClick={() => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)}
              style={{ visibility: lightboxImages.length > 1 ? 'visible' : 'hidden' }}
            >
              &#8250;
            </button>
            {lightboxImages.length > 1 && (
              <div className="lightbox-counter">
                {lightboxIndex + 1} / {lightboxImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicReportPage;
